// Admin-only "G1 Health" — FLEET-WIDE view of the relay's outbound Kalshi API
// load, scored in TOKENS vs each account's per-bucket tier limit (Kalshi's
// actual rate-limit currency), with an org drill-down. Fills the gap the
// caller-scoped Phase-2 StatusBar leaves.
//
// Kalshi rate limits (docs/getting_started/rate_limits): two token buckets PER
// ACCOUNT — read & write — default 10 tokens/request. Per-second refill by tier:
//   Basic    read 200  write 100
//   Advanced read 300  write 300
// The limits are PER ACCOUNT, so the meaningful signal is each account's % of
// its OWN limit — a fleet token SUM is meaningless (no fleet-wide bucket). The
// trend tracks the peak account's % of limit (all orgs, or the selected one).
//
// Source: GET /api/admin/g1-health (admin-gated) — per-account load + 429 state
// + tier + org names + the write-429 tape, reusing the relay's
// getKalshiLoadStats()/getOrgCacheHealth()/getWrite429Log().
//
// LANE CAVEAT: reflects whichever relay this build talks to (g1.pm = staging
// load; gbuff = live trader load).

import { Fragment, useEffect, useRef, useState } from 'react';
import { Thermometer, AlertTriangle } from 'lucide-react';
import { apiFetch } from '@/stores/authStore';
import { getFeedTransports } from '@/hooks/useHydra';

interface RateLimitState {
  rateLimitedUntil: number;
  rateLimitStrikes: number;
  msUntilClear: number;
  throttled: boolean;
  keyPoolMode: string;
  channelCount: number;
  quarantinedCount: number;
}
interface RequestStats {
  windowMs: number;
  total: number;
  perSecond: number;
  byBucket: { trading: number; critical: number; general: number };
  // MEASURED token spend (relay ≥ token-accounting build): per-endpoint costs
  // from the relay's static table, split by Kalshi's real read/write buckets
  // (HTTP method). Absent against an older relay → fall back to count × 10.
  read?: { requests: number; tokensPerSecond: number };
  write?: { requests: number; tokensPerSecond: number };
  byEndpoint?: Array<{
    endpoint: string;
    rw: 'read' | 'write';
    count: number;
    tokens: number;
  }>;
}
// Watch-driven /account/limits sample — Kalshi's OWN statement of the
// account's buckets (tier + capacity + refill/sec). No tokens-remaining gauge
// exists in the payload; the value here is ground-truthing the LIMITS we
// score against (refill_rate replaces the tier-map guess as the denominator).
interface LimitsSample {
  sampledAt: number;
  ageMs: number;
  raw: unknown;
  parsed: {
    usageTier: string;
    read: { bucketCapacity: number; refillRate: number } | null;
    write: { bucketCapacity: number; refillRate: number } | null;
  } | null;
}
interface AccountLoad {
  key: string; // "org:<id>" | "user:<id>"
  ageMs: number;
  tier?: string; // "basic" | "advanced"
  rateLimit: RateLimitState;
  requests: RequestStats;
  limits?: LimitsSample | null;
}
interface OrgCacheHealth {
  polling: boolean;
  orgs: Array<{
    orgId: string;
    ageMs: number;
    ok: boolean;
    consecutiveFailures: number;
    orderCount: number;
    suspectedEmptyOverwrites: number;
  }>;
}
interface Write429Entry {
  ts: number;
  userId: string;
  username: string | null;
  scope: string | null;
  orgId: string | null;
  op: string;
  action: string | null;
  ticker: string | null;
  orderId: string | null;
  message: string;
}
// Live DB connection-pool utilization (relay-side). `waiting` > 0 is the
// saturation signal — requests queued because every connection up to `max` is
// checked out. The shared pool carries reads/pollers/ledger folds; the critical
// pool carries only the order path (isolated so a read storm can't starve it).
interface PoolStat {
  max: number;
  total: number;
  idle: number;
  inUse: number;
  waiting: number;
}
interface PoolStats {
  shared: PoolStat | null;
  critical: PoolStat | null;
  serverMax: number;
}
// Read-endpoint failures (GET /api/admin/g1-health → readFailures): open-orders /
// positions / fills GETs that 502'd. Falls through both other failure surfaces —
// the reject panel logs placements, the write-429 tape logs write-429s; a read
// 401/5xx/pool-stall writes nothing and shows nowhere else. In-memory, last 50.
interface ReadFailEntry {
  ts: number;
  userId: string;
  username: string | null;
  endpoint: string; // 'orders' | 'snapshot' | 'fills'
  status: number | null; // upstream HTTP status when determinable; null for a non-HTTP stall
  message: string;
}
// Feed-liveness board inputs (relay ≥ token-accounting build; all optional so
// the page renders against an older relay).
interface OrgWsHealth {
  orgId: string;
  connected: boolean;
  subscribed: boolean;
  lastEventAgeMs: number | null;
  reconnects: number;
  seqGaps: number;
  fillsIngested: number;
  keyLabel: string | null;
}
interface FillsPollerInfo {
  orgId: string;
  lastPollAt: number | null;
  lastInserted: number;
  mode: 'primary' | 'backstop';
}
interface PushHealth {
  connections: number;
  users: number;
  sent: number;
  suppressed: number;
  resolverErrors: number;
}
interface SweepHealth {
  running: boolean;
  lastRunAt: number | null;
  lastDurationMs: number | null;
  lastTickersChecked: number;
  lastSettlementsCredited: number;
  terminalTotal: number;
  consecutiveFailures: number;
}
interface ShadowHealth {
  running: boolean;
  runs: number;
  usersChecked: number;
  divergences: number;
  lastRunAt: number | null;
  lastDivergenceAt: number | null;
}
interface G1HealthSnapshot {
  serverTs: number;
  kalshiLoad: AccountLoad[];
  orgCache: OrgCacheHealth;
  write429?: Write429Entry[];
  readFailures?: ReadFailEntry[];
  poolStats?: PoolStats;
  orgNames?: Record<string, string>;
  kalshiWs?: OrgWsHealth[];
  fillsPoller?: FillsPollerInfo[];
  push?: PushHealth;
  settlementSweep?: SweepHealth;
  positionStates?: { mode: string; shadow: ShadowHealth };
  orgPollMs?: number;
}

// Durable reject history (GET /api/admin/rejected-orders) — read back from the
// Postgres `trades` audit table, so it survives relay restarts and captures
// EVERY Kalshi rejection reason, not just 429s (unlike the in-memory write429
// tape). This is the forensic view: "what actually failed this morning?"
// The structured cap-breach the relay records on a limit-gate reject. `current`
// is split into openContracts (settled-exclusion inflation) vs intentContracts
// (stale in-flight buys) so a false reject is diagnosable from the log alone.
interface RejectBreach {
  rule?: string;
  cap?: number;
  current?: number;
  requested?: number;
  openContracts?: number;
  intentContracts?: number;
  openPositions?: number;
}
interface RejectEntry {
  ts: number;
  userId: string;
  username: string | null;
  orgId: string | null;
  scope: string;
  action: string | null; // trader-native buy/sell from the logged request body
  ticker: string | null;
  side: string | null;
  count: number | null;
  priceDollars: string | null;
  message: string | null;
  code: string | null; // LIMIT_* rejection code when the pre-trade gate blocked it
  breach: RejectBreach | null;
}
interface RejectErrorGroup {
  reason: string; // the rejection code, or a number-normalized message
  sample: string | null; // a representative full message for the group
  count: number;
  lastTs: number;
}
interface RejectSnapshot {
  serverTs: number;
  windowMs: number;
  total: number;
  shown: number;
  byError: RejectErrorGroup[];
  recent: RejectEntry[];
  orgNames?: Record<string, string>;
}

// Live cap-inspector (GET /api/admin/trader-cap-debug) — decomposes a trader's
// current cap usage so a miscount's source is visible. inactiveExcluded vs
// tickerUniverse is the settled-exclusion health signal.
interface CapDebugVenue {
  openContracts: number;
  openPositions: number;
  inactiveExcluded: number;
  tickerUniverse: number;
  positions: { ticker: string; side: string; netCount: number }[];
}
interface CapDebug {
  serverTs: number;
  userId: string;
  username: string | null;
  orgId: string | null;
  orgName: string | null;
  caps: { maxContracts: number | null; maxPositions: number | null };
  intentContracts: number;
  committedContracts: number;
  committedPositions: number;
  kalshi: CapDebugVenue;
  poly: CapDebugVenue;
}

const POLL_MS = 3000;
const REJECT_POLL_MS = 15000; // durable reject history — heavier DB read, slower cadence
const REJECT_WINDOWS: { label: string; ms: number }[] = [
  { label: '1h', ms: 3_600_000 },
  { label: '6h', ms: 6 * 3_600_000 },
  { label: '24h', ms: 24 * 3_600_000 },
  { label: '3d', ms: 3 * 24 * 3_600_000 },
];
const HISTORY = 80; // sparkline samples (~4 min at 3s)
const TOKEN_COST = 10; // Kalshi default tokens/request (non-default endpoints exist)

// Per-second token limits by tier (read / write). The relay only auto-upgrades
// to Advanced, so Basic/Advanced are the live cases; unknown → assume Basic.
const TIER_LIMITS: Record<string, { read: number; write: number }> = {
  basic: { read: 200, write: 100 },
  advanced: { read: 300, write: 300 },
};
function tierLimit(tier?: string) {
  return TIER_LIMITS[tier ?? 'basic'] ?? TIER_LIMITS.basic;
}

interface Enriched extends AccountLoad {
  readTokS: number;
  writeTokS: number;
  readPct: number;
  writePct: number;
  lim: { read: number; write: number };
  /** true when readTokS/writeTokS are relay-measured per-endpoint tokens
   *  (vs the legacy count × 10 estimate against an older relay). */
  measured: boolean;
}
function enrich(a: AccountLoad): Enriched {
  const rq = a.requests;
  const winSec = (rq?.windowMs || 10000) / 1000;
  // Prefer the relay's MEASURED per-endpoint token spend; fall back to the
  // count × 10 estimate so the page still works against an older relay
  // mid-deploy.
  const measured = rq?.read != null && rq?.write != null;
  const reads = (rq?.byBucket?.critical ?? 0) + (rq?.byBucket?.general ?? 0);
  const writes = rq?.byBucket?.trading ?? 0;
  const readTokS = measured ? rq.read!.tokensPerSecond : (reads / winSec) * TOKEN_COST;
  const writeTokS = measured ? rq.write!.tokensPerSecond : (writes / winSec) * TOKEN_COST;
  // Denominator: Kalshi's own sampled refill rates when the ground-truth
  // sampler has run; the tier-map guess otherwise.
  const sampled = a.limits?.parsed;
  const guess = tierLimit(a.tier);
  const lim = {
    read: sampled?.read?.refillRate ?? guess.read,
    write: sampled?.write?.refillRate ?? guess.write,
  };
  return {
    ...a,
    readTokS,
    writeTokS,
    readPct: Math.round((readTokS / lim.read) * 100),
    writePct: Math.round((writeTokS / lim.write) * 100),
    lim,
    measured,
  };
}

function loadTone(pct: number, throttled = false): string {
  if (throttled || pct >= 100) return 'text-red-400';
  if (pct >= 75) return 'text-amber-400';
  return 'text-foreground';
}
function shortKey(key: string): { scope: string; id: string } {
  const [scope, id = ''] = key.split(':');
  return { scope, id: id.length > 10 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id };
}
function orgIdOf(key: string): string | null {
  return key.startsWith('org:') ? key.slice(4) : null;
}
function fmtAge(ms: number): string {
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m${String(s % 60).padStart(2, '0')}`;
}

// Area sparkline. preserveAspectRatio="none" stretches the viewBox to full width
// (fine for line/area); the readout lives in the HTML caption, NOT as SVG text
// (which would smear). `refValue` draws a dashed danger line (e.g. the 100%
// limit). non-scaling-stroke keeps lines crisp under the stretch.
function Sparkline({
  values,
  color,
  height = 48,
  refValue,
}: {
  values: number[];
  color: string;
  height?: number;
  refValue?: number;
}) {
  const W = 600;
  const H = height;
  const max = Math.max(1, refValue ?? 0, ...values);
  const n = values.length;
  const y = (v: number) => H - (v / max) * (H - 4) - 2;
  const xs = n <= 1 ? [0, W] : values.map((_, i) => (i / (n - 1)) * W);
  const ys = n <= 1 ? [y(values[0] ?? 0), y(values[0] ?? 0)] : values.map(y);
  const line = xs.map((xv, i) => `${i === 0 ? 'M' : 'L'}${xv.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const area = `${line} L${W},${H} L0,${H} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" height={H} role="img">
      <line
        x1={0}
        y1={H - 0.5}
        x2={W}
        y2={H - 0.5}
        stroke="currentColor"
        className="text-border"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      {refValue != null && (
        <line
          x1={0}
          y1={y(refValue)}
          x2={W}
          y2={y(refValue)}
          stroke="#ef4444"
          strokeWidth={1}
          strokeDasharray="4 3"
          opacity={0.6}
          vectorEffect="non-scaling-stroke"
        />
      )}
      <path d={area} fill={color} fillOpacity={0.12} />
      <path d={line} fill="none" stroke={color} strokeWidth={1.75} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'danger' | 'warn' | 'normal' }) {
  const color = tone === 'danger' ? 'text-red-400' : tone === 'warn' ? 'text-amber-400' : 'text-foreground';
  return (
    <div className="border-border rounded-lg border px-4 py-3">
      <div className="text-muted-foreground text-[11px] uppercase tracking-wide">{label}</div>
      <div className={`mt-1 font-mono text-2xl font-semibold ${color}`}>{value}</div>
      {sub && <div className="text-muted-foreground mt-0.5 text-[11px]">{sub}</div>}
    </div>
  );
}

// Three at-a-glance failure counters — the top-of-page summary row. Each card is
// a count + status + one line of the dominant detail, for a failure class with
// its own source: rejected PLACEMENTS (durable, trades table), write-429s
// (in-mem tape), and read-endpoint 502s (in-mem tape). The full Rejected-orders
// forensic panel + cap inspector lives directly below this row; the two
// in-memory tapes are summarized here only (their standalone tables were dropped).
function FailCard({
  title,
  count,
  tone,
  badge,
  badgeTone,
  sub1,
  sub2,
}: {
  title: string;
  count: number | string;
  tone: string;
  badge?: string;
  badgeTone?: string;
  sub1?: string;
  sub2?: string;
}) {
  return (
    <div className="border-border rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-muted-foreground text-xs font-semibold">{title}</h3>
        {badge ? <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${badgeTone}`}>{badge}</span> : null}
      </div>
      <div className={`mt-1 font-mono text-2xl font-bold ${tone}`}>{count}</div>
      {sub1 ? <div className="text-muted-foreground text-[11px]">{sub1}</div> : null}
      {sub2 ? (
        <div className="text-foreground/80 mt-0.5 truncate text-[11px]" title={sub2}>
          {sub2}
        </div>
      ) : null}
    </div>
  );
}

function FailureCards({
  rej,
  rejErr,
  rejWindowMs,
  write429,
  readFailures,
  serverTs,
}: {
  rej: RejectSnapshot | null;
  rejErr: string | null;
  rejWindowMs: number;
  write429: Write429Entry[];
  readFailures: ReadFailEntry[];
  serverTs: number;
}) {
  const winLabel =
    REJECT_WINDOWS.find((w) => w.ms === rejWindowMs)?.label ?? `${Math.round(rejWindowMs / 3_600_000)}h`;
  const topReject = rej?.byError?.[0];

  // Largest read-fail group (account · endpoint · status) so the card names the
  // dominant offender, not just a raw event count.
  let rfTop: { key: string; n: number } | null = null;
  const rfGroups = new Map<string, number>();
  for (const e of readFailures) {
    const who = e.username ?? e.userId.slice(0, 8);
    const k = `${who} · ${e.endpoint} · ${e.status ?? 'stall'}`;
    const n = (rfGroups.get(k) ?? 0) + 1;
    rfGroups.set(k, n);
    if (!rfTop || n > rfTop.n) rfTop = { key: k, n };
  }

  const w0 = write429[0];
  const wAcct = w0 ? (w0.username ?? shortKey(`${w0.scope ?? 'user'}:${w0.userId}`).id) : null;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <FailCard
        title="Rejected Kalshi orders"
        badge="DURABLE"
        badgeTone="bg-red-600/80 text-white"
        count={rej ? rej.total : rejErr ? '—' : '…'}
        tone={rej && rej.total > 0 ? 'text-red-400' : rej ? 'text-green-400' : 'text-muted-foreground'}
        sub1={rejErr ? 'load failed' : `last ${winLabel}`}
        sub2={
          topReject ? `top ${topReject.reason} ×${topReject.count}` : rej && rej.total === 0 ? 'none 👍' : undefined
        }
      />
      <FailCard
        title="Recent write 429s"
        badge="IN-MEM"
        badgeTone="bg-muted text-muted-foreground"
        count={write429.length}
        tone={write429.length > 0 ? 'text-amber-400' : 'text-green-400'}
        sub1="last 20 · cleared on restart"
        sub2={
          w0
            ? `${wAcct} · ${w0.op}${w0.action ? ` ${w0.action}` : ''} · ${fmtAge(Math.max(0, serverTs - w0.ts))} ago`
            : 'none 👍'
        }
      />
      <FailCard
        title="Read-endpoint failures"
        badge="IN-MEM"
        badgeTone="bg-muted text-muted-foreground"
        count={readFailures.length}
        tone={readFailures.length > 0 ? 'text-amber-400' : 'text-green-400'}
        sub1="last 50 · cleared on restart"
        sub2={rfTop ? `${rfTop.key} ×${rfTop.n}` : 'none 👍'}
      />
    </div>
  );
}

// Live cap decomposition for one trader. Answers WHY the cap `current` is what
// it is: the split across kalshi open / poly open / in-flight intents, and the
// settled-market exclude-set size (inactiveExcluded). An empty exclude set while
// at/near the cap is the false-reject signature.
function CapInspector({
  cap,
  err,
  loading,
  onClose,
}: {
  cap: CapDebug | null;
  err: string | null;
  loading: boolean;
  onClose: () => void;
}) {
  const num = (n: number) => n.toLocaleString();
  const atContractCap =
    cap != null && cap.caps.maxContracts != null && cap.committedContracts >= cap.caps.maxContracts;
  const nearContractCap =
    cap != null &&
    cap.caps.maxContracts != null &&
    cap.committedContracts >= cap.caps.maxContracts * 0.9;
  // The known false-reject signature: pressed against the contract cap while the
  // settled-market exclude set is empty — if the trader holds resolved markets,
  // those are counting because the /markets read that clears them came back empty.
  const suspectExclusionMiss = nearContractCap && cap != null && cap.kalshi.inactiveExcluded === 0;

  return (
    <div className="border-border bg-muted/30 mt-3 rounded-lg border p-4">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-foreground text-sm font-semibold">
          Cap inspector{cap ? ` — ${cap.username ?? cap.userId}` : ''}
          {cap?.orgName ? <span className="text-muted-foreground ml-1 font-normal">· {cap.orgName}</span> : null}
        </h3>
        <button
          onClick={onClose}
          className="border-border text-muted-foreground hover:text-foreground ml-auto rounded border px-2 py-0.5 text-[11px]"
        >
          close
        </button>
      </div>

      {err ? (
        <p className="py-2 text-center text-xs text-red-400">Couldn&apos;t load: {err}</p>
      ) : !cap ? (
        <p className="text-muted-foreground py-2 text-center text-xs">{loading ? 'Reading live cap state…' : '—'}</p>
      ) : (
        <div className="space-y-3 text-xs">
          {suspectExclusionMiss && (
            <div className="flex items-start gap-2 rounded bg-amber-500/10 px-3 py-2 text-amber-300">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                At/near the contract cap with <span className="font-semibold">0 settled markets excluded</span>. If
                this trader holds resolved/voided markets, they&apos;re still counting toward the cap — the settled-
                exclusion read (Kalshi /markets) likely came back empty. This is the known false-reject bug; it bites
                hardest during a morning rate-limit crunch. Re-open this during the failure window to confirm.
              </span>
            </div>
          )}

          {/* Contract cap decomposition — the headline. */}
          <div>
            <div className="text-muted-foreground mb-1 text-[11px] font-medium uppercase tracking-wide">
              Contract cap
            </div>
            <div className="font-mono">
              <span className={atContractCap ? 'font-semibold text-red-400' : 'text-foreground'}>
                {num(cap.committedContracts)}
              </span>
              <span className="text-muted-foreground"> / {cap.caps.maxContracts == null ? '∞' : num(cap.caps.maxContracts)}</span>
              <span className="text-muted-foreground">
                {'  ='} kalshi open {num(cap.kalshi.openContracts)} + poly open {num(cap.poly.openContracts)} + intents{' '}
                {num(cap.intentContracts)}
              </span>
            </div>
          </div>

          {/* Position cap decomposition. */}
          <div>
            <div className="text-muted-foreground mb-1 text-[11px] font-medium uppercase tracking-wide">
              Position cap
            </div>
            <div className="font-mono">
              <span className="text-foreground">{num(cap.committedPositions)}</span>
              <span className="text-muted-foreground"> / {cap.caps.maxPositions == null ? '∞' : num(cap.caps.maxPositions)}</span>
              <span className="text-muted-foreground">
                {'  '}(distinct ticker|side across venues + intents)
              </span>
            </div>
          </div>

          {/* Settled-exclusion health per venue. */}
          <div>
            <div className="text-muted-foreground mb-1 text-[11px] font-medium uppercase tracking-wide">
              Settled-market exclusion (health)
            </div>
            <table className="w-full text-left font-mono">
              <thead className="text-muted-foreground border-border border-b">
                <tr>
                  <th className="py-1 pr-3 font-medium">Venue</th>
                  <th className="px-3 text-right font-medium" title="net contracts counting toward the cap">Open ct</th>
                  <th className="px-3 text-right font-medium" title="distinct open ticker|side">Open pos</th>
                  <th className="px-3 text-right font-medium" title="settled/voided tickers excluded from the cap">Excluded</th>
                  <th className="px-3 text-right font-medium" title="all tickers ever in the fills ledger">Universe</th>
                </tr>
              </thead>
              <tbody>
                {(['kalshi', 'poly'] as const).map((v) => {
                  const ven = cap[v];
                  const empty = ven.inactiveExcluded === 0 && ven.tickerUniverse > ven.openPositions;
                  return (
                    <tr key={v} className="border-border/50 border-b">
                      <td className="text-foreground py-1 pr-3">{v}</td>
                      <td className="text-foreground px-3 text-right">{num(ven.openContracts)}</td>
                      <td className="text-foreground px-3 text-right">{num(ven.openPositions)}</td>
                      <td className={`px-3 text-right ${empty ? 'font-semibold text-amber-400' : 'text-muted-foreground'}`}>
                        {num(ven.inactiveExcluded)}
                      </td>
                      <td className="text-muted-foreground px-3 text-right">{num(ven.tickerUniverse)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Biggest contributors — eyeball what's actually counting. */}
          {cap.kalshi.positions.length > 0 && (
            <div>
              <div className="text-muted-foreground mb-1 text-[11px] font-medium uppercase tracking-wide">
                Top Kalshi positions counting toward the cap ({cap.kalshi.openPositions})
              </div>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-left font-mono">
                  <tbody>
                    {cap.kalshi.positions.map((p, i) => (
                      <tr key={`${p.ticker}-${p.side}-${i}`} className="border-border/50 border-b">
                        <td className="text-foreground py-0.5 pr-3">{p.ticker}</td>
                        <td className="text-muted-foreground px-3">{p.side}</td>
                        <td className="text-foreground px-3 text-right">{num(p.netCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="text-muted-foreground text-[11px]">
            Live snapshot, server clock {new Date(cap.serverTs).toLocaleTimeString()}. Reading this triggers a fresh
            Kalshi /markets read, so mid-day it usually reads healthy — the durable reject breach above is the record
            of what actually happened during the failure.
          </p>
        </div>
      )}
    </div>
  );
}

// Durable reject history from Postgres. Unlike WriteLog (live, in-memory, 429s
// only, wiped on restart) this reads the persisted `trades` audit table, so it
// answers "what actually failed this morning?" across ALL rejection reasons and
// survives redeploys. Window is selectable; the "by reason" rollup makes the
// dominant failure obvious at a glance.
function RejectedOrders({
  snap,
  err,
  loading,
  windowMs,
  setWindowMs,
  selectedOrg,
  fallbackOrgNames,
}: {
  snap: RejectSnapshot | null;
  err: string | null;
  loading: boolean;
  windowMs: number;
  setWindowMs: (ms: number) => void;
  selectedOrg: string;
  fallbackOrgNames: Record<string, string>;
}) {
  const orgNames = snap?.orgNames ?? fallbackOrgNames;
  const inSel = (orgId: string | null) => selectedOrg === 'all' || orgId === selectedOrg;
  const recent = (snap?.recent ?? []).filter((r) => inSel(r.orgId));
  const byError = snap?.byError ?? [];
  const serverTs = snap?.serverTs ?? Date.now();
  const winLabel = REJECT_WINDOWS.find((w) => w.ms === windowMs)?.label ?? `${Math.round(windowMs / 3_600_000)}h`;

  // Live cap inspector — opened by clicking a trader in the tape.
  const [inspectUser, setInspectUser] = useState<string | null>(null);
  const [cap, setCap] = useState<CapDebug | null>(null);
  const [capErr, setCapErr] = useState<string | null>(null);
  const [capLoading, setCapLoading] = useState(false);
  const inspect = async (userId: string) => {
    setInspectUser(userId);
    setCap(null);
    setCapErr(null);
    setCapLoading(true);
    try {
      const res = await apiFetch(`/api/admin/trader-cap-debug?userId=${encodeURIComponent(userId)}`);
      if (!res.ok) {
        setCapErr(`HTTP ${res.status}`);
        return;
      }
      setCap((await res.json()) as CapDebug);
    } catch (e) {
      setCapErr(e instanceof Error ? e.message : 'request failed');
    } finally {
      setCapLoading(false);
    }
  };

  return (
    <section className="border-border rounded-lg border p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 className="text-foreground text-sm font-semibold">Rejected Kalshi orders</h2>
        <span className="rounded bg-red-600/80 px-1.5 py-0.5 text-[10px] font-bold text-white">DURABLE</span>
        <span className="text-muted-foreground text-[11px]">
          from the trades audit table — every rejection reason, survives relay restarts
        </span>
        <div className="ml-auto flex items-center gap-1">
          {REJECT_WINDOWS.map((w) => (
            <button
              key={w.label}
              onClick={() => setWindowMs(w.ms)}
              className={`rounded border px-2 py-0.5 text-[11px] ${
                w.ms === windowMs
                  ? 'border-orange-400 bg-orange-400/15 text-orange-300'
                  : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {err ? (
        <p className="py-3 text-center text-xs text-red-400">Couldn&apos;t load reject history: {err}</p>
      ) : !snap ? (
        <p className="text-muted-foreground py-3 text-center text-xs">{loading ? 'Loading…' : 'No data.'}</p>
      ) : (
        <>
          <p className="text-muted-foreground mb-3 text-xs">
            {selectedOrg === 'all' ? (
              <>
                <span className={snap.total > 0 ? 'font-semibold text-red-400' : 'font-semibold text-green-400'}>
                  {snap.total}
                </span>{' '}
                rejected fleet-wide in the last {winLabel}
                {snap.shown < snap.total ? ` · showing latest ${snap.shown}` : ''}
              </>
            ) : (
              <>
                <span className={recent.length > 0 ? 'font-semibold text-red-400' : 'font-semibold text-green-400'}>
                  {recent.length}
                </span>{' '}
                shown for this org · {snap.total} fleet-wide in the last {winLabel}
              </>
            )}
          </p>

          {snap.total === 0 ? (
            <p className="text-muted-foreground py-3 text-center text-xs">
              No rejected Kalshi orders in the last {winLabel}. 👍
            </p>
          ) : (
            <div className="space-y-4">
              {/* Rollup by exact error message — fleet-wide over the full window,
                  so a burst's dominant cause is obvious even if the tape is capped. */}
              <div>
                <h3 className="text-muted-foreground mb-1 text-[11px] font-medium uppercase tracking-wide">
                  By reason (fleet, last {winLabel})
                </h3>
                <table className="w-full text-left text-xs">
                  <thead className="text-muted-foreground border-border border-b">
                    <tr>
                      <th className="py-1.5 pr-3 text-right font-medium">Count</th>
                      <th className="px-3 font-medium">Reason</th>
                      <th className="px-3 text-right font-medium">Last</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {byError.map((g, i) => (
                      <tr key={`${g.reason}-${i}`} className="border-border/50 border-b">
                        <td className="text-foreground py-1.5 pr-3 text-right font-semibold">{g.count}</td>
                        <td className="px-3" title={g.sample ?? g.reason}>
                          <span className="text-red-400">{g.reason}</span>
                          {g.sample && g.sample !== g.reason && (
                            <span className="text-muted-foreground ml-2">
                              {g.sample.length > 80 ? `${g.sample.slice(0, 80)}…` : g.sample}
                            </span>
                          )}
                        </td>
                        <td className="text-muted-foreground whitespace-nowrap px-3 text-right">
                          {g.lastTs ? `${fmtAge(Math.max(0, serverTs - g.lastTs))} ago` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Recent tape — respects the org drill-down. */}
              <div>
                <h3 className="text-muted-foreground mb-1 text-[11px] font-medium uppercase tracking-wide">
                  Recent ({recent.length})
                </h3>
                {recent.length === 0 ? (
                  <p className="text-muted-foreground py-2 text-center text-xs">None for this org in-window.</p>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="text-muted-foreground border-border sticky top-0 border-b bg-[var(--background)]">
                        <tr>
                          <th className="py-1.5 pr-3 font-medium">When</th>
                          <th className="px-3 font-medium">Account</th>
                          <th className="px-3 font-medium">Order</th>
                          <th className="px-3 font-medium">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono">
                        {recent.map((e, i) => {
                          const acct = e.username ?? shortKey(`${e.scope}:${e.userId}`).id;
                          const orgLabel = e.orgId ? orgNames[e.orgId] ?? shortKey(`org:${e.orgId}`).id : null;
                          return (
                            <tr key={`${e.ts}-${i}`} className="border-border/50 border-b">
                              <td className="text-muted-foreground whitespace-nowrap py-1.5 pr-3">
                                {fmtAge(Math.max(0, serverTs - e.ts))} ago
                              </td>
                              <td className="px-3">
                                <span className="text-muted-foreground">{e.scope}</span>
                                <button
                                  onClick={() => inspect(e.userId)}
                                  className="text-foreground hover:text-orange-300 ml-1 underline decoration-dotted underline-offset-2"
                                  title="Inspect this trader's live cap decomposition"
                                >
                                  {acct}
                                </button>
                                {orgLabel && <span className="text-muted-foreground ml-1">· {orgLabel}</span>}
                              </td>
                              <td className="text-foreground whitespace-nowrap px-3">
                                {e.action ?? '—'}
                                {e.side ? ` ${e.side}` : ''}
                                {e.count != null ? ` ×${e.count}` : ''}
                                {e.ticker ? <span className="text-muted-foreground ml-1">{e.ticker}</span> : null}
                              </td>
                              <td className="px-3" title={e.message ?? ''}>
                                <div className="text-red-400">
                                  {e.message
                                    ? e.message.length > 70
                                      ? `${e.message.slice(0, 70)}…`
                                      : e.message
                                    : '—'}
                                </div>
                                {/* Cap-breach decomposition — the reason `current` was
                                    what it was, split into its inflation sources. */}
                                {e.breach && e.breach.rule === 'LIMIT_MAX_CONTRACTS' && (
                                  <div className="text-muted-foreground text-[10px]">
                                    cur {e.breach.current ?? '?'} = open {e.breach.openContracts ?? '?'} · intent{' '}
                                    {e.breach.intentContracts ?? '?'} / cap {e.breach.cap ?? '?'}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {inspectUser && (
                <CapInspector cap={cap} err={capErr} loading={capLoading} onClose={() => setInspectUser(null)} />
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

interface LoadSample {
  fleetPeak: number; // worst account's max(read%,write%) across the fleet
  byOrg: Record<string, number>; // per-org worst account %
}

// Live feed transport — CLIENT-SIDE (this browser/tab's hydra WS), polled from
// the useHydra singleton. The headline diagnostic for the "every reload/window
// forms a new connection" report: SharedWorker = one upstream shared across all
// this browser's tabs; direct = a separate WS per window (the fallback where
// `type:'module'` SharedWorkers aren't supported, e.g. Safari). Reflects only the
// browser you're viewing this in — load it where the symptom is seen.
function transportBadge(t: 'shared' | 'direct' | 'connecting') {
  if (t === 'shared') return { cls: 'bg-green-600/80 text-white', label: 'SharedWorker' };
  if (t === 'direct') return { cls: 'bg-amber-500/80 text-white', label: 'direct WS' };
  return { cls: 'bg-muted text-muted-foreground', label: 'connecting' };
}
function FeedTransport() {
  const [feeds, setFeeds] = useState(getFeedTransports);
  useEffect(() => {
    const id = setInterval(() => setFeeds(getFeedTransports()), 2000);
    return () => clearInterval(id);
  }, []);
  const anyDirect = feeds.some((f) => f.transport === 'direct');
  const allShared = feeds.length > 0 && feeds.every((f) => f.transport === 'shared');
  return (
    <section className="border-border rounded-lg border p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 className="text-foreground text-sm font-semibold">Live feed transport</h2>
        {feeds.length > 0 && (
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
              anyDirect ? 'bg-amber-500/80 text-white' : allShared ? 'bg-green-600/80 text-white' : 'bg-muted text-muted-foreground'
            }`}
          >
            {anyDirect ? 'DIRECT — one WS per window' : allShared ? 'SHARED — one WS across tabs' : 'CONNECTING'}
          </span>
        )}
        <span className="text-muted-foreground text-[11px]">
          this browser&apos;s hydra feed · SharedWorker shares one upstream across all your tabs; a direct fallback
          (Safari / unsupported) opens a separate socket per window — the &ldquo;new connection every reload/window&rdquo; symptom
        </span>
      </div>
      {feeds.length === 0 ? (
        <p className="text-muted-foreground py-3 text-center text-xs">
          No feed client on this tab yet — still connecting, or the desk shell isn&apos;t mounted here.
        </p>
      ) : (
        <table className="w-full text-left text-xs font-mono">
          <thead className="text-muted-foreground border-border border-b">
            <tr>
              <th className="py-1.5 pr-3 font-medium">Feed</th>
              <th className="px-3 font-medium">Role</th>
              <th className="px-3 font-medium">Transport</th>
              <th className="px-3 text-center font-medium" title="upstream WS connected">Live</th>
              <th className="px-3 font-medium">Upstream</th>
            </tr>
          </thead>
          <tbody>
            {feeds.map((f) => {
              const t = transportBadge(f.transport);
              return (
                <tr key={f.id} className="border-border/50 border-b">
                  <td className="text-foreground py-1.5 pr-3">{f.id}</td>
                  <td className="text-muted-foreground px-3">{f.role}</td>
                  <td className="px-3">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${t.cls}`}>{t.label}</span>
                  </td>
                  <td className="px-3 text-center">
                    {f.connected ? <span className="text-green-400">●</span> : <span className="text-muted-foreground">○</span>}
                  </td>
                  <td className="text-muted-foreground max-w-[280px] truncate px-3" title={f.url}>
                    {f.url}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

// DB connection pool — relay-side Postgres pool utilization, the system-plumbing
// companion to the feed transport above. The whole point of the 2026-07-01 fix
// lives here: the ORDER PATH runs on the isolated `critical` pool, so even when
// the `shared` pool (reads / pollers / whale-ledger folds) saturates at an event
// burst, placement still gets a connection. `waiting` > 0 = that pool is at cap
// and requests are queuing. Per-lane, like the rest of this page.
function poolTone(inUse: number, max: number, waiting: number): string {
  if (waiting > 0 || (max > 0 && inUse >= max)) return 'text-red-400';
  if (max > 0 && inUse >= max * 0.75) return 'text-amber-400';
  return 'text-green-400';
}
function PoolRow({ label, desc, s }: { label: string; desc: string; s: PoolStat | null }) {
  if (!s) {
    return (
      <tr className="border-border/50 border-b">
        <td className="text-foreground py-1.5 pr-3">{label}</td>
        <td className="text-muted-foreground px-3 text-[11px]" colSpan={5}>
          not initialized yet (no query has hit this pool)
        </td>
      </tr>
    );
  }
  const pct = s.max > 0 ? Math.round((s.inUse / s.max) * 100) : 0;
  const tone = poolTone(s.inUse, s.max, s.waiting);
  const bar =
    s.waiting > 0 || (s.max > 0 && s.inUse >= s.max)
      ? 'bg-red-500'
      : s.max > 0 && s.inUse >= s.max * 0.75
        ? 'bg-amber-500'
        : 'bg-green-500';
  return (
    <tr className={`border-border/50 border-b ${s.waiting > 0 ? 'bg-red-500/10' : ''}`}>
      <td className="py-1.5 pr-3">
        <span className="text-foreground">{label}</span>
        <span className="text-muted-foreground ml-2 text-[10px]">{desc}</span>
      </td>
      <td className="px-3 text-right">
        <span className={`font-semibold ${tone}`}>{s.inUse}</span>
        <span className="text-muted-foreground">/{s.max}</span>
      </td>
      <td className="text-muted-foreground px-3 text-right">{s.idle}</td>
      <td className={`px-3 text-right ${s.waiting > 0 ? 'font-semibold text-red-400' : 'text-muted-foreground'}`}>
        {s.waiting}
      </td>
      <td className="px-3">
        <div className="bg-muted h-2 w-full overflow-hidden rounded" title={`${s.inUse} of ${s.max} in use`}>
          <div className={`h-full ${bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      </td>
      <td className={`px-3 text-right font-mono ${tone}`}>{pct}%</td>
    </tr>
  );
}
function DbPool({ stats }: { stats?: PoolStats }) {
  if (!stats) return null;
  const openTotal = (stats.shared?.total ?? 0) + (stats.critical?.total ?? 0);
  const anyWaiting = (stats.shared?.waiting ?? 0) + (stats.critical?.waiting ?? 0) > 0;
  return (
    <section className="border-border rounded-lg border p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 className="text-foreground text-sm font-semibold">DB connection pool</h2>
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
            anyWaiting ? 'bg-red-600/80 text-white' : 'bg-green-600/80 text-white'
          }`}
        >
          {anyWaiting ? 'QUEUING' : 'HEALTHY'}
        </span>
        <span className="text-muted-foreground text-[11px]">
          this relay&apos;s Postgres pools · the order path runs on the isolated{' '}
          <span className="text-foreground">critical</span> pool so a read storm on{' '}
          <span className="text-foreground">shared</span> can&apos;t starve placement · waiting &gt; 0 = at cap
        </span>
      </div>
      <table className="w-full text-left text-xs font-mono">
        <thead className="text-muted-foreground border-border border-b">
          <tr>
            <th className="py-1.5 pr-3 font-medium">Pool</th>
            <th className="px-3 text-right font-medium" title="checked out right now / configured max">In use</th>
            <th className="px-3 text-right font-medium" title="idle open connections, ready for instant checkout">Idle</th>
            <th
              className="px-3 text-right font-medium"
              title="requests queued for a connection — >0 means this pool is saturated"
            >
              Waiting
            </th>
            <th className="px-3 font-medium">Utilization</th>
            <th className="px-3 text-right font-medium" aria-label="percent" />
          </tr>
        </thead>
        <tbody>
          <PoolRow label="shared" desc="reads · pollers · ledger folds" s={stats.shared} />
          <PoolRow label="critical" desc="order path — auth · limits · logTrade" s={stats.critical} />
        </tbody>
      </table>
      <p className="text-muted-foreground mt-2 text-[11px]">
        {openTotal} open of server max_connections={stats.serverMax} (shared with the box&apos;s other tenants).
        Reflects this build&apos;s relay (g1.pm = staging; gbuff = live). Watch for{' '}
        <span className="text-foreground">shared</span> waiting climbing while{' '}
        <span className="text-foreground">critical</span> stays flat — that&apos;s reads degrading while orders keep
        flowing, i.e. the pool-isolation fix doing its job.
      </p>
    </section>
  );
}

// Feed liveness — the "is the pipeline alive end to end?" board. Absorbs the
// old Org-broker-cache table and joins it with the WS fills sockets and the
// REST fills backstop into ONE row per org, under a tile row for the push
// channel / settlement sweeper / shadow verifier. This is the real-time
// answer to "are the feeds live?" without ssh-ing into a box.
function FeedLiveness({
  snap,
  orgNames,
  inSel,
}: {
  snap: G1HealthSnapshot;
  orgNames: Record<string, string>;
  inSel: (orgId: string | null) => boolean;
}) {
  const push = snap.push;
  const sweep = snap.settlementSweep;
  const shadow = snap.positionStates?.shadow;
  const mode = snap.positionStates?.mode;

  // One row per org: union of the three per-org health sources.
  const byOrg = new Map<
    string,
    {
      poller?: OrgCacheHealth['orgs'][number];
      ws?: OrgWsHealth;
      fills?: FillsPollerInfo;
    }
  >();
  const rowFor = (orgId: string) => {
    const r = byOrg.get(orgId) ?? {};
    byOrg.set(orgId, r);
    return r;
  };
  for (const o of snap.orgCache.orgs) if (inSel(o.orgId)) rowFor(o.orgId).poller = o;
  for (const w of snap.kalshiWs ?? []) if (inSel(w.orgId)) rowFor(w.orgId).ws = w;
  for (const f of snap.fillsPoller ?? []) if (inSel(f.orgId)) rowFor(f.orgId).fills = f;
  const rows = [...byOrg.entries()].sort((a, b) => {
    const an = orgNames[a[0]] ?? a[0];
    const bn = orgNames[b[0]] ?? b[0];
    return an.localeCompare(bn);
  });

  const suppressionPct =
    push && push.sent + push.suppressed > 0
      ? Math.round((push.suppressed / (push.sent + push.suppressed)) * 100)
      : null;
  const pollMs = snap.orgPollMs;

  return (
    <section className="border-border rounded-lg border p-4">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-foreground text-sm font-semibold">Feed liveness</h2>
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
            snap.orgCache.polling ? 'bg-green-600/80 text-white' : 'bg-muted text-muted-foreground'
          }`}
        >
          {snap.orgCache.polling ? 'POLLING' : 'IDLE'}
        </span>
        <span className="text-muted-foreground text-[11px]">
          per org: shared {pollMs != null ? `${pollMs}ms` : ''} order/queue poller · WS fills socket · REST fills
          backstop
        </span>
      </div>

      {/* Pipeline tile row — the fan-out side (relay → browsers) + background loops. */}
      <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat
          label="Push channel"
          value={push ? `${push.connections}` : '—'}
          sub={push ? `conns · ${push.users} users` : 'not reported'}
        />
        <Stat
          label="Push suppression"
          value={suppressionPct != null ? `${suppressionPct}%` : '—'}
          sub={push ? `${push.sent.toLocaleString()} sent · ${push.suppressed.toLocaleString()} suppressed` : undefined}
        />
        <Stat
          label="Resolver errors"
          value={push ? String(push.resolverErrors) : '—'}
          tone={push && push.resolverErrors > 0 ? 'danger' : 'normal'}
          sub="push topic resolves failed"
        />
        <Stat
          label="Settlement sweep"
          value={sweep ? (sweep.running ? 'on' : 'off') : '—'}
          tone={sweep && sweep.consecutiveFailures > 0 ? 'warn' : 'normal'}
          sub={
            sweep?.lastRunAt
              ? `${fmtAge(Math.max(0, snap.serverTs - sweep.lastRunAt))} ago · ${sweep.lastSettlementsCredited} credited${sweep.consecutiveFailures > 0 ? ` · ${sweep.consecutiveFailures} fails` : ''}`
              : 'no run yet'
          }
        />
        <Stat
          label="Shadow parity"
          value={shadow ? String(shadow.divergences) : '—'}
          tone={shadow && shadow.divergences > 0 ? 'danger' : 'normal'}
          sub={shadow ? `divergences · mode ${mode ?? '?'} · ${shadow.usersChecked} checked` : undefined}
        />
      </div>

      {rows.length === 0 ? (
        <p className="text-muted-foreground py-3 text-center text-xs">No active orgs right now.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono">
            <thead className="text-muted-foreground border-border border-b">
              <tr>
                <th className="py-1.5 pr-3 font-medium">Org</th>
                <th className="px-3 text-right font-medium">Orders cached</th>
                <th className="px-3 text-center font-medium" title="order/queue poller snapshot state">Poller</th>
                <th className="px-3 text-right font-medium" title="poller snapshot age — should hover near the poll cadence">Age</th>
                <th className="px-3 text-right font-medium" title="consecutive failed polls">Fails</th>
                <th className="px-3 text-right font-medium" title="empty-overwrite guards tripped">Empty-guards</th>
                <th className="px-3 text-center font-medium" title="Kalshi user-fills WebSocket">WS fills</th>
                <th className="px-3 text-right font-medium" title="WS: reconnects · sequence gaps">rc/gaps</th>
                <th className="px-3 text-right font-medium" title="fills ingested over this socket's lifetime">WS fills #</th>
                <th className="px-3 text-center font-medium" title="REST fills poller: primary (WS down/off) or backstop (WS healthy)">REST fills</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([orgId, r]) => {
                const stale = r.poller && !r.poller.ok;
                const wsUp = r.ws?.connected && r.ws?.subscribed;
                return (
                  <tr key={orgId} className={`border-border/50 border-b ${stale ? 'bg-red-500/10' : ''}`}>
                    <td className="text-foreground py-1.5 pr-3">{orgNames[orgId] ?? shortKey(`org:${orgId}`).id}</td>
                    <td className="text-foreground px-3 text-right">{r.poller?.orderCount ?? '—'}</td>
                    <td className="px-3 text-center">
                      {r.poller ? (
                        r.poller.ok ? (
                          <span className="text-green-400">ok</span>
                        ) : (
                          <span className="font-bold text-red-400">STALE</span>
                        )
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="text-muted-foreground px-3 text-right">
                      {r.poller ? fmtAge(r.poller.ageMs) : '—'}
                    </td>
                    <td className={`px-3 text-right ${r.poller && r.poller.consecutiveFailures > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                      {r.poller?.consecutiveFailures ?? '—'}
                    </td>
                    <td className={`px-3 text-right ${r.poller && r.poller.suspectedEmptyOverwrites > 0 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                      {r.poller?.suspectedEmptyOverwrites ?? '—'}
                    </td>
                    <td className="px-3 text-center">
                      {r.ws ? (
                        wsUp ? (
                          <span
                            className="text-green-400"
                            title={`key ${r.ws.keyLabel ?? '?'} · last event ${r.ws.lastEventAgeMs != null ? fmtAge(r.ws.lastEventAgeMs) : '—'} ago`}
                          >
                            LIVE
                          </span>
                        ) : (
                          <span className="font-bold text-red-400" title="socket down or not subscribed — REST poller should be primary">
                            DOWN
                          </span>
                        )
                      ) : (
                        <span className="text-muted-foreground" title="KALSHI_WS_FILLS off or no socket for this org">—</span>
                      )}
                    </td>
                    <td className={`px-3 text-right ${r.ws && (r.ws.seqGaps > 0 || r.ws.reconnects > 0) ? 'text-amber-400' : 'text-muted-foreground'}`}>
                      {r.ws ? `${r.ws.reconnects}/${r.ws.seqGaps}` : '—'}
                    </td>
                    <td className="text-muted-foreground px-3 text-right">{r.ws?.fillsIngested ?? '—'}</td>
                    <td className="px-3 text-center">
                      {r.fills ? (
                        <span
                          className={r.fills.mode === 'backstop' ? 'text-green-400' : 'text-amber-400'}
                          title={`last poll ${r.fills.lastPollAt ? fmtAge(Math.max(0, snap.serverTs - r.fills.lastPollAt)) + ' ago' : 'never'} · last inserted ${r.fills.lastInserted}. backstop = WS is primary (good); primary = REST carrying fills (WS down/off).`}
                        >
                          {r.fills.mode}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-muted-foreground mt-2 text-[11px]">
        Healthy shape: poller <span className="text-foreground">ok</span> with age ≈ the poll cadence, WS{' '}
        <span className="text-foreground">LIVE</span> with rc/gaps 0/0, REST fills{' '}
        <span className="text-foreground">backstop</span>, suppression high (quiet books send nothing), resolver
        errors 0, shadow divergences 0.
      </p>
    </section>
  );
}

export default function G1Health() {
  const [snap, setSnap] = useState<G1HealthSnapshot | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<string>('all');
  // Per-account drilldown: which account row's per-endpoint table is open.
  const [openAccount, setOpenAccount] = useState<string | null>(null);
  const loadHist = useRef<LoadSample[]>([]);
  const cancelled = useRef(false);
  // Durable reject history — independent fetch (its own window + slower poll).
  const [rej, setRej] = useState<RejectSnapshot | null>(null);
  const [rejErr, setRejErr] = useState<string | null>(null);
  const [rejWindowMs, setRejWindowMs] = useState<number>(6 * 3_600_000);

  useEffect(() => {
    cancelled.current = false;
    const load = async () => {
      try {
        const res = await apiFetch('/api/admin/g1-health');
        if (!res.ok) {
          if (!cancelled.current) setErr(`HTTP ${res.status}`);
          return;
        }
        const data = (await res.json()) as G1HealthSnapshot;
        if (cancelled.current) return;
        // Accumulate the % of limit (peak account), fleet-wide and per-org, so
        // the trend stays meaningful under the org drill-down.
        const byOrg: Record<string, number> = {};
        let fleetPeak = 0;
        for (const a of data.kalshiLoad) {
          const e = enrich(a);
          const pct = Math.max(e.readPct, e.writePct);
          fleetPeak = Math.max(fleetPeak, pct);
          const oid = orgIdOf(a.key);
          if (oid) byOrg[oid] = Math.max(byOrg[oid] ?? 0, pct);
        }
        loadHist.current = [...loadHist.current, { fleetPeak, byOrg }].slice(-HISTORY);
        setSnap(data);
        setErr(null);
      } catch (e) {
        if (!cancelled.current) setErr(e instanceof Error ? e.message : 'request failed');
      }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => {
      cancelled.current = true;
      clearInterval(id);
    };
  }, []);

  // Durable reject history — re-fetches on window change, then polls slowly.
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const res = await apiFetch(`/api/admin/rejected-orders?windowMs=${rejWindowMs}&limit=500`);
        if (!res.ok) {
          if (!cancel) setRejErr(`HTTP ${res.status}`);
          return;
        }
        const data = (await res.json()) as RejectSnapshot;
        if (cancel) return;
        setRej(data);
        setRejErr(null);
      } catch (e) {
        if (!cancel) setRejErr(e instanceof Error ? e.message : 'request failed');
      }
    };
    load();
    const id = setInterval(load, REJECT_POLL_MS);
    return () => {
      cancel = true;
      clearInterval(id);
    };
  }, [rejWindowMs]);

  const orgNames = snap?.orgNames ?? {};
  const enriched = (snap?.kalshiLoad ?? []).map(enrich); // whole fleet
  // Org drill-down: 'all' or a specific orgId. Selecting an org also hides
  // personal (user:) accounts.
  const inSel = (orgId: string | null) => selectedOrg === 'all' || orgId === selectedOrg;
  const filtered = enriched.filter((e) => inSel(orgIdOf(e.key)));
  const sorted = [...filtered].sort((a, b) => Math.max(b.readPct, b.writePct) - Math.max(a.readPct, a.writePct));
  const peakRead = filtered.reduce((m, e) => Math.max(m, e.readPct), 0);
  const peakWrite = filtered.reduce((m, e) => Math.max(m, e.writePct), 0);
  const throttledCount = filtered.filter((e) => e.rateLimit?.throttled).length;
  const write429 = (snap?.write429 ?? []).filter((w) => inSel(w.orgId));

  // Dropdown options = orgs actually present in the data, labeled by name.
  const presentOrgIds = new Set<string>();
  for (const e of enriched) {
    const id = orgIdOf(e.key);
    if (id) presentOrgIds.add(id);
  }
  for (const o of snap?.orgCache.orgs ?? []) presentOrgIds.add(o.orgId);
  for (const w of snap?.write429 ?? []) if (w.orgId) presentOrgIds.add(w.orgId);
  const orgOptions = [...presentOrgIds]
    .map((id) => ({ id, label: orgNames[id] ?? shortKey(`org:${id}`).id }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Trend series: peak % of limit, respecting the org selection.
  const series = loadHist.current.map((s) => (selectedOrg === 'all' ? s.fleetPeak : s.byOrg[selectedOrg] ?? 0));
  const curLoad = series.length ? series[series.length - 1] : 0;
  const peakLoad = Math.max(0, ...series);

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="space-y-5">
        <header>
          <div className="flex items-center gap-2">
            <Thermometer size={20} className="text-orange-400" />
            <h1 className="text-foreground text-xl font-bold">G1 Health</h1>
            <span className="text-muted-foreground ml-2 text-xs">
              Per-account Kalshi load in <span className="text-foreground">tokens</span>, vs each account&apos;s
              tier limit
            </span>
            {snap && (
              <select
                value={selectedOrg}
                onChange={(ev) => setSelectedOrg(ev.target.value)}
                className="border-border bg-muted text-foreground ml-auto rounded-md border px-2 py-1 text-xs"
                title="Drill into one org, or show the whole fleet"
              >
                <option value="all">All orgs ({orgOptions.length})</option>
                {orgOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </div>
          <p className="text-muted-foreground mt-2 text-xs leading-snug">
            Kalshi meters two per-account token buckets — read &amp; write, ~{TOKEN_COST} tokens/request.
            Limits/sec: <span className="text-foreground">Basic 200r/100w</span>,{' '}
            <span className="text-foreground">Advanced 300r/300w</span>. % = token rate ÷ that account&apos;s
            limit. Read/write buckets are independent — a 429 backoff sheds reads only, never order placement
            (writes). Reflects this build&apos;s relay (g1.pm = staging; gbuff = live trader load).
          </p>
        </header>

        {err && (
          <div className="flex items-center gap-2 rounded-lg bg-red-500/10 px-4 py-2.5 text-sm text-red-400">
            <AlertTriangle size={15} /> Couldn&apos;t load metrics: {err}
          </div>
        )}

        {/* Client-side — this browser's hydra transport, independent of the
            relay metrics fetch below, so it shows even if /g1-health errors. */}
        <FeedTransport />

        {/* System plumbing — relay DB connection-pool utilization, sitting with
            the feed transport. Renders once the /g1-health snapshot lands. */}
        <DbPool stats={snap?.poolStats} />

        {/* Fleet load summary — lifted above the reject history so the
            at-a-glance account / load / backoff counts lead the page. */}
        {snap && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={selectedOrg === 'all' ? 'Active accounts' : 'Accounts in org'} value={String(filtered.length)} />
            <Stat
              label="Peak read load"
              value={`${peakRead}%`}
              sub="of an account's read limit"
              tone={peakRead >= 100 ? 'danger' : peakRead >= 75 ? 'warn' : 'normal'}
            />
            <Stat
              label="Peak write load"
              value={`${peakWrite}%`}
              sub="of an account's write limit"
              tone={peakWrite >= 100 ? 'danger' : peakWrite >= 75 ? 'warn' : 'normal'}
            />
            <Stat
              label="In 429 backoff"
              value={String(throttledCount)}
              tone={throttledCount > 0 ? 'danger' : 'normal'}
            />
          </div>
        )}

        {/* Failure summary — three small counters (rejected placements · write-429s
            · read-endpoint 502s), each from its own source. The full Rejected-orders
            detail with the cap inspector follows directly below. */}
        <FailureCards
          rej={rej}
          rejErr={rejErr}
          rejWindowMs={rejWindowMs}
          write429={write429}
          readFailures={snap?.readFailures ?? []}
          serverTs={snap?.serverTs ?? Date.now()}
        />

        {/* Durable reject history — its own fetch, so it renders even if the
            live /g1-health snapshot errors. The primary forensic panel. */}
        <RejectedOrders
          snap={rej}
          err={rejErr}
          loading={!rej && !rejErr}
          windowMs={rejWindowMs}
          setWindowMs={setRejWindowMs}
          selectedOrg={selectedOrg}
          fallbackOrgNames={snap?.orgNames ?? {}}
        />

        {!snap && !err && <p className="text-muted-foreground py-12 text-center text-sm">Loading fleet…</p>}

        {snap && (
          <>
            <section className="border-border rounded-lg border p-4">
              <div className="mb-2 flex items-baseline justify-between">
                <h2 className="text-foreground text-sm font-semibold">
                  Peak load — % of limit
                  <span className="text-muted-foreground ml-2 text-[11px] font-normal">
                    {selectedOrg === 'all' ? 'worst account across the fleet' : 'worst account in this org'} ·
                    100% = at the bucket limit
                  </span>
                </h2>
                <span className="text-muted-foreground font-mono text-xs">
                  now {curLoad}% · peak {peakLoad}% · last ~{Math.round((HISTORY * POLL_MS) / 60000)}m
                </span>
              </div>
              <Sparkline
                values={series}
                color={peakLoad >= 100 ? '#ef4444' : peakLoad >= 75 ? '#f59e0b' : '#3b82f6'}
                refValue={100}
              />
            </section>

            <section className="border-border rounded-lg border p-4">
              <div className="mb-3 flex items-baseline justify-between">
                <h2 className="text-foreground text-sm font-semibold">Per-account token load</h2>
                <span className="text-muted-foreground text-[11px]">
                  tokens/sec over the relay&apos;s 10s window · sorted by tightest bucket · click a row for the
                  per-endpoint spend
                </span>
              </div>
              {sorted.length === 0 ? (
                <p className="text-muted-foreground py-6 text-center text-xs">
                  {selectedOrg === 'all'
                    ? 'No active account clients right now (the relay caches one per org pool / personal account; an idle relay shows none).'
                    : 'No active account for this org right now.'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="text-muted-foreground border-border border-b">
                      <tr>
                        <th className="py-1.5 pr-3 font-medium">Account</th>
                        <th className="px-3 font-medium">Tier</th>
                        <th className="px-3 text-right font-medium" title="read tokens/sec · % of read limit">Read</th>
                        <th className="px-3 text-right font-medium" title="write tokens/sec · % of write limit">Write</th>
                        <th
                          className="px-3 text-right font-medium"
                          title="Kalshi's own /account/limits statement: read/write refill per second (+ burst capacity). Sampled while this page is open; the % columns score against these when present."
                        >
                          Bucket (live)
                        </th>
                        <th className="px-3 text-center font-medium" title="read backoff active — writes still flow">429</th>
                        <th className="px-3 text-right font-medium" title="backoff escalation depth">Strikes</th>
                        <th className="px-3 text-right font-medium" title="ms until backoff clears">Clears in</th>
                        <th className="px-3 text-right font-medium" title="live / total keys">Keys</th>
                        <th className="px-3 text-right font-medium">Age</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      {sorted.map((e) => {
                        const k = shortKey(e.key);
                        const oid = orgIdOf(e.key);
                        const name = oid ? orgNames[oid] ?? k.id : k.id;
                        const rl = e.rateLimit;
                        const lp = e.limits?.parsed ?? null;
                        const isOpen = openAccount === e.key;
                        const byEndpoint = e.requests?.byEndpoint ?? [];
                        const totalTok = byEndpoint.reduce((s, be) => s + be.tokens, 0);
                        return (
                          <Fragment key={e.key}>
                            <tr
                              onClick={() => setOpenAccount(isOpen ? null : e.key)}
                              className={`border-border/50 hover:bg-muted/40 cursor-pointer border-b ${rl.throttled ? 'bg-red-500/10' : ''}`}
                              title="Click for the per-endpoint token spend"
                            >
                              <td className="py-1.5 pr-3">
                                <span className="text-muted-foreground">{isOpen ? '▾' : '▸'} {k.scope}</span>
                                <span className="text-foreground ml-1">{name}</span>
                              </td>
                              <td className="px-3">
                                <span className={e.tier === 'advanced' ? 'text-green-400' : 'text-amber-400'}>
                                  {e.tier ?? 'basic'}
                                </span>
                              </td>
                              <td
                                className={`px-3 text-right ${loadTone(e.readPct, rl.throttled)}`}
                                title={`${e.readTokS.toFixed(0)} of ${e.lim.read} read tok/s${e.measured ? ' (measured per-endpoint costs)' : ' (≈ requests × 10 estimate)'}`}
                              >
                                {e.readTokS.toFixed(0)}
                                <span className="text-muted-foreground">t/s</span>{' '}
                                <span className="font-semibold">{e.readPct}%</span>
                              </td>
                              <td
                                className={`px-3 text-right ${loadTone(e.writePct)}`}
                                title={`${e.writeTokS.toFixed(0)} of ${e.lim.write} write tok/s — writes are never paused by the read backoff`}
                              >
                                {e.writeTokS.toFixed(0)}
                                <span className="text-muted-foreground">t/s</span>{' '}
                                <span className="font-semibold">{e.writePct}%</span>
                              </td>
                              <td className="px-3 text-right">
                                {lp?.read && lp?.write ? (
                                  <span
                                    title={`Kalshi /account/limits (${fmtAge(e.limits?.ageMs ?? 0)} old): tier ${lp.usageTier} · read ${lp.read.refillRate}/s burst ${lp.read.bucketCapacity} · write ${lp.write.refillRate}/s burst ${lp.write.bucketCapacity}`}
                                  >
                                    <span className="text-foreground">{lp.read.refillRate}r/{lp.write.refillRate}w</span>
                                    <span className="text-muted-foreground"> ✓</span>
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground" title="No /account/limits sample yet — % scored against the tier-map guess">
                                    —
                                  </span>
                                )}
                              </td>
                              <td className="px-3 text-center">
                                {rl.throttled ? (
                                  <span className="rounded bg-red-600/80 px-1.5 py-0.5 text-[10px] font-bold text-white">
                                    THROTTLED
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className={`px-3 text-right ${rl.rateLimitStrikes > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                                {rl.rateLimitStrikes}
                              </td>
                              <td className="text-muted-foreground px-3 text-right">
                                {rl.msUntilClear > 0 ? `${(rl.msUntilClear / 1000).toFixed(1)}s` : '—'}
                              </td>
                              <td className={`px-3 text-right ${rl.quarantinedCount > 0 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                                {rl.channelCount - rl.quarantinedCount}/{rl.channelCount}
                              </td>
                              <td className="text-muted-foreground px-3 text-right">{fmtAge(e.ageMs)}</td>
                            </tr>
                            {isOpen && (
                              <tr className="border-border/50 border-b">
                                <td colSpan={10} className="bg-muted/20 px-4 py-2">
                                  {byEndpoint.length === 0 ? (
                                    <span className="text-muted-foreground text-[11px]">
                                      No per-endpoint data (relay predates token accounting, or no requests in the
                                      10s window).
                                    </span>
                                  ) : (
                                    <>
                                      <table className="w-auto min-w-[520px] text-left text-[11px]">
                                        <thead className="text-muted-foreground">
                                          <tr>
                                            <th className="py-1 pr-4 font-medium">Endpoint</th>
                                            <th className="pr-4 font-medium">r/w</th>
                                            <th className="pr-4 text-right font-medium">req / 10s</th>
                                            <th className="pr-4 text-right font-medium">tok/s</th>
                                            <th className="text-right font-medium">% of spend</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {byEndpoint.map((be) => (
                                            <tr key={`${be.rw}:${be.endpoint}`}>
                                              <td className="text-foreground py-0.5 pr-4">{be.endpoint}</td>
                                              <td className={`pr-4 ${be.rw === 'write' ? 'text-sky-400' : 'text-muted-foreground'}`}>
                                                {be.rw}
                                              </td>
                                              <td className="text-foreground pr-4 text-right">{be.count}</td>
                                              <td className="text-foreground pr-4 text-right">
                                                {(be.tokens / ((e.requests?.windowMs ?? 10000) / 1000)).toFixed(1)}
                                              </td>
                                              <td className="text-muted-foreground text-right">
                                                {totalTok > 0 ? Math.round((be.tokens / totalTok) * 100) : 0}%
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                      <p className="text-muted-foreground mt-1.5 text-[11px]">
                                        Batching check: <span className="text-foreground">portfolio/orders</span> and{' '}
                                        <span className="text-foreground">portfolio/orders/queue_positions</span> counts
                                        should match ~1:1 regardless of how many orders are resting — one batched queue
                                        call per poll tick is the whole portfolio. A queue count ≫ orders count means
                                        un-batched per-order reads are sneaking in.
                                      </p>
                                    </>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <FeedLiveness snap={snap} orgNames={orgNames} inSel={inSel} />

            <p className="text-muted-foreground text-[11px]">
              server clock {new Date(snap.serverTs).toLocaleTimeString()} · polling every {POLL_MS / 1000}s ·
              token rates use the relay&apos;s measured per-endpoint costs (older relays fall back to ≈ requests ×{' '}
              {TOKEN_COST})
            </p>
          </>
        )}
      </div>
    </div>
  );
}
